import api from '../api/api';

// PhonePe Payment Service (Exclusive Payment Gateway)
// This application uses PhonePe OAuth v2 API for all payment transactions
// Handles payment order creation and status checking

/**
 * Get PhonePe configuration
 * Returns: { provider, environment, merchantId, isConfigured }
 * @returns {Promise} - Promise that resolves with PhonePe config
 */
export const getPhonePeConfig = async () => {
  try {
    const response = await api.get('/payments/config');
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching PhonePe config:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to fetch payment config'
    };
  }
};

/**
 * Create a PhonePe payment order
 * @param {Object} orderDetails - Order details
 * @param {number} orderDetails.amount - Payment amount in rupees
 * @param {string} orderDetails.planName - Plan name
 * @param {string} orderDetails.planId - Plan ID
 * @returns {Promise} - Promise that resolves with payment URL
 */
export const createPaymentOrder = async (orderDetails) => {
  try {
    const response = await api.post('/payments/create-order', orderDetails);
    // Backend already returns {success: true, data: {...}}
    // Don't wrap it again!
    return response.data;
  } catch (error) {
    console.error('Payment order creation failed:', error.response?.data);
    
    // Parse error response for specific error codes
    const errorData = error.response?.data;
    const errorCode = errorData?.code;
    const errorMessage = errorData?.error;
    
    let userFriendlyMessage;
    
    // Provide specific error messages based on error code
    if (errorCode === 'PHONEPE_NOT_CONFIGURED' || (errorMessage && errorMessage.includes('not configured'))) {
      userFriendlyMessage = 'Payment system is not configured. Please contact support.';
    } else if (errorCode === 'AUTH_FAILED' || (errorMessage && errorMessage.includes('authentication'))) {
      userFriendlyMessage = 'Payment service authentication failed. Please contact support.';
    } else if (errorCode === 'NETWORK_ERROR') {
      userFriendlyMessage = 'Unable to connect to payment service. Please check your internet connection and try again.';
    } else {
      userFriendlyMessage = errorMessage || 'Failed to create payment order';
    }
    
    return {
      success: false,
      error: userFriendlyMessage,
      code: errorCode,
      originalError: errorMessage  // Preserve original error for debugging
    };
  }
};

/**
 * Check payment status
 * @param {string} merchantOrderId - PhonePe order ID
 * @returns {Promise} - Promise that resolves with payment status
 */
export const checkPaymentStatus = async (merchantOrderId) => {
  try {
    const response = await api.post('/payments/status', {
      merchantOrderId
    });
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error checking payment status:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to check payment status'
    };
  }
};

/**
 * Open PhonePe payment page
 * @param {string} paymentUrl - PhonePe payment URL
 * @param {string} merchantOrderId - Order ID for polling
 * @returns {Promise} - Promise that resolves when payment is complete
 */
export const openPhonePePayment = async (paymentUrl, merchantOrderId) => {
  return new Promise((resolve, reject) => {
    try {
      // Redirect to payment URL in the same window
      // PhonePe will redirect back to our callback URL after payment
      window.location.href = paymentUrl;
      
      // Since we're redirecting away, resolve immediately
      // The actual payment verification will happen on the callback page
      resolve({
        success: true,
        data: {
          redirected: true,
          merchantOrderId
        }
      });
    } catch (error) {
      console.error('Payment redirect error:', error);
      reject(error);
    }
  });
};

/**
 * Initiate payment flow
 * @param {Object} orderDetails - Order details
 * @returns {Promise} - Promise that resolves when payment is complete
 */
export const initiatePayment = async (orderDetails) => {
  try {
    // Step 1: Create payment order

    const orderResult = await createPaymentOrder(orderDetails);
    
    console.log('🔍 FULL API RESPONSE:', JSON.stringify(orderResult, null, 2));
    console.log('🔍 orderResult.success:', orderResult.success);
    console.log('🔍 orderResult.data:', orderResult.data);
    console.log('🔍 orderResult.data type:', typeof orderResult.data);
    console.log('🔍 orderResult.data keys:', orderResult.data ? Object.keys(orderResult.data) : 'null');
    
    if (!orderResult.success) {
      throw new Error(orderResult.error);
    }

    // Extract payment URL from response (consistent wrapped contract)
    const paymentUrl = orderResult.data?.paymentUrl;
    const merchantOrderId = orderResult.data?.merchantOrderId;

    console.log('🔍 Extracted paymentUrl:', paymentUrl);
    console.log('🔍 Extracted merchantOrderId:', merchantOrderId);

    // Validate payment URL
    if (!paymentUrl) {
      console.error('❌ Payment URL is missing from response:', orderResult);
      console.error('❌ Full response stringified:', JSON.stringify(orderResult, null, 2));
      throw new Error('Payment URL not received from server. Please try again.');
    }

    if (!merchantOrderId) {
      console.error('❌ Order ID is missing from response:', orderResult);
      throw new Error('Order ID not received from server. Please try again.');
    }

    // Step 2: Open payment page and wait for completion

    const paymentResult = await openPhonePePayment(paymentUrl, merchantOrderId);

    return {
      success: true,
      data: {
        merchantOrderId,
        redirected: true,
        message: 'Redirecting to payment page'
      }
    };

  } catch (error) {
    console.error('❌ Payment failed:', error);
    return {
      success: false,
      error: error.message || 'Payment failed'
    };
  }
};

export default {
  getPhonePeConfig,
  createPaymentOrder,
  checkPaymentStatus,
  openPhonePePayment,
  initiatePayment
};
